/**
 * PortfolioNavItem — the "My inventions" entry point (contract row S4, fig 5).
 *
 * WHY THIS IS A COMPONENT AND NOT A ROW IN A NAV ARRAY
 * ----------------------------------------------------
 * The design says the inventor "reaches the map from the sidebar, not by typing
 * a URL". The M2 browser walk types the URL, so that sentence had no mechanism
 * behind it until the sidebar entry became a contracted, walked part of S4. This
 * component is that mechanism: it carries its own data-part root, so the walk
 * can assert the words "My inventions" are VISIBLE inside it on the live screen.
 *
 * The wording is verbatim from the design mockup and must not be "tidied".
 */
import { NavLink } from 'react-router-dom'
import { LayoutGrid } from 'lucide-react'

export const PORTFOLIO_NAV_LABEL = 'My inventions'
export const PORTFOLIO_NAV_TO = '/projects?view=atlas'

export interface PortfolioNavItemProps {
  /** How many inventions the set holds. Rendered as a count pill when supplied. */
  count?: number
  /** Sidebar collapsed state — hides the label the same way Sidebar's links do. */
  collapsed?: boolean
  /** Closes the mobile drawer, matching the sibling links in Sidebar.tsx. */
  onNavigate?: () => void
  /** Highlights the item while the own-scope map is the active view. */
  active?: boolean
  /**
   * Where it is being rendered. 'sidebar' is the dark navy shell of Figure 5's
   * left rail; 'rail' is the same entry point sitting on the light page body
   * above the view tabs. The wording and the link are identical — only the
   * palette changes, because white-on-navy text on a light page would fail both
   * the contrast guard and the figure it is scored against.
   */
  variant?: 'sidebar' | 'rail'
}

export default function PortfolioNavItem({
  count,
  collapsed = false,
  onNavigate,
  active = false,
  variant = 'sidebar',
}: PortfolioNavItemProps) {
  const onDark = variant === 'sidebar'
  const linkClass = onDark
    ? `relative flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition-colors
       ${active ? 'bg-white/15 font-medium text-white' : 'text-white/75 hover:bg-white/10 hover:text-white'}`
    : `relative flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm transition-colors
       ${active ? 'bg-iflow-100 font-semibold text-iflow-900' : 'text-iflow-800 hover:bg-iflow-50 hover:text-iflow-900'}`
  const countClass = onDark
    ? 'ml-auto shrink-0 rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-medium text-white/90'
    : 'ml-1 shrink-0 rounded-full bg-iflow-100 px-2 py-0.5 text-[11px] font-medium text-iflow-900'
  return (
    <li data-part="PortfolioNavItem">
      <NavLink
        to={PORTFOLIO_NAV_TO}
        onClick={onNavigate}
        title={collapsed ? PORTFOLIO_NAV_LABEL : undefined}
        aria-label={PORTFOLIO_NAV_LABEL}
        className={linkClass}
      >
        {active && onDark && (
          <span
            className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r bg-iflow-400"
            aria-hidden
          />
        )}
        <LayoutGrid size={18} aria-hidden className="shrink-0" />
        {!collapsed && <span className="truncate">My inventions</span>}
        {!collapsed && typeof count === 'number' && (
          <span className={countClass}>
            {count}
          </span>
        )}
      </NavLink>
    </li>
  )
}
